import { PageHeader } from '@/shared/components/ui/PageHeader';
// BizPlus parity Phase 4.2 — target setting and achievement.
//
// A reps × types grid for the selected month, inline-editable, with copy-forward
// from the previous month and an achievement view showing target vs actual, run
// rate and projected close.
//
// A rep with no target set shows "—", never 0%. Displaying 0% would present a
// management omission as a performance failure.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Target as TargetIcon, CopyCheck, TrendingUp, Trophy, AlertTriangle } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR } from '@/shared/utils/currency';

type TargetType = 'enquiry' | 'quotation' | 'sales' | 'invoice' | 'calling';

interface Rep { _id: string; name: string; department?: string }
interface Cell { _id?: string; monthlyTargetCount: number; monthlyTargetAmount: number }
interface GridResponse {
  period: string; types: TargetType[]; reps: Rep[];
  cells: Record<string, Cell>; canSetTeam: boolean;
}
interface Metric {
  target: number; achieved: number; percent: number | null; gap: number | null;
  runRate: number | null; projectedClose: number | null; onTrack: boolean | null;
}
interface AchievementRow {
  representativeId: string; name: string; hasTarget: boolean;
  progress: { total: number; elapsed: number; remaining: number; state: string };
  count: Metric; amount: Metric;
}
interface AchievementResponse {
  period: string; type: TargetType; rows: AchievementRow[];
  summary: { count: { target: number; achieved: number; percent: number | null } } | null;
  unsupported?: boolean; reason?: string;
}

/** This month as YYYY-MM, from LOCAL parts so it matches the server's key. */
function thisPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const pct = (v: number | null) => (v === null || v === undefined ? '—' : `${v}%`);

export function TargetsPage() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState(thisPeriod());
  const [type, setType] = useState<TargetType>('sales');
  const [view, setView] = useState<'grid' | 'achievement' | 'leaderboard'>('grid');
  const [edits, setEdits] = useState<Record<string, { count?: number; amount?: number }>>({});

  const grid = useQuery({
    queryKey: ['targets', period, type],
    queryFn: async () => (await api.get<GridResponse>(`/api/targets?period=${period}&type=${type}`)).data,
  });

  const achievement = useQuery({
    queryKey: ['targets-achievement', period, type],
    queryFn: async () => (await api.get<AchievementResponse>(
      `/api/targets/achievement?period=${period}&type=${type}`,
    )).data,
    enabled: view === 'achievement',
  });

  const leaderboard = useQuery({
    queryKey: ['targets-leaderboard', period, type],
    queryFn: async () => (await api.get<{ rows: any[]; unsupported?: boolean }>(
      `/api/targets/leaderboard?period=${period}&type=${type}`,
    )).data,
    enabled: view === 'leaderboard',
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['targets'] });
    qc.invalidateQueries({ queryKey: ['targets-achievement'] });
    qc.invalidateQueries({ queryKey: ['targets-leaderboard'] });
    setEdits({});
  };

  const save = useMutation({
    mutationFn: async ({ repId, count, amount }: { repId: string; count: number; amount: number }) =>
      (await api.put('/api/targets', {
        representativeId: repId, type, period,
        monthlyTargetCount: count, monthlyTargetAmount: amount,
      })).data,
    onSuccess: () => { refresh(); toast.success('Target saved.'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the target.')),
  });

  const copyForward = useMutation({
    mutationFn: async () => (await api.post('/api/targets/copy-forward', { toPeriod: period })).data,
    onSuccess: (d: any) => {
      refresh();
      // Say what was skipped: a silent "copied 0" looks like a failure when it
      // actually means the month was already set.
      toast.success(
        `Copied ${d.copied} target(s) from ${d.from}.`
        + (d.skipped ? ` ${d.skipped} left untouched (already set).` : ''),
      );
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Copy forward failed.')),
  });

  const cellFor = (repId: string): Cell =>
    grid.data?.cells?.[`${repId}:${type}`] ?? { monthlyTargetCount: 0, monthlyTargetAmount: 0 };

  const dirty = useMemo(() => Object.keys(edits).length > 0, [edits]);

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={TargetIcon} eyebrow="Sales" title="Sales Targets" description={<>Monthly targets per representative, and how the month is actually tracking.</>} actions={<><div className="flex flex-wrap items-end gap-2">
          <label className="text-body-sm">
            <span className="mb-1 block text-on-surface-variant">Month</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value || thisPeriod())}
              className="h-9 rounded border border-outline bg-surface px-2 text-data-md" />
          </label>
          <label className="text-body-sm">
            <span className="mb-1 block text-on-surface-variant">Type</span>
            <select value={type} onChange={(e) => setType(e.target.value as TargetType)}
              className="h-9 rounded border border-outline bg-surface px-2 text-data-md">
              {(grid.data?.types ?? ['enquiry', 'quotation', 'sales', 'invoice', 'calling']).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>
          {grid.data?.canSetTeam && (
            <Button size="sm" variant="secondary" onClick={() => copyForward.mutate()} disabled={copyForward.isPending}>
              <CopyCheck className="h-4 w-4 mr-1" /> Copy from previous month
            </Button>
          )}
        </div></>} />

      <div className="flex gap-1">
        {([['grid', 'Set targets', TargetIcon], ['achievement', 'Achievement', TrendingUp], ['leaderboard', 'Leaderboard', Trophy]] as const)
          .map(([key, label, Icon]) => (
            <Button key={key} size="sm" variant={view === key ? 'secondary' : 'ghost'} onClick={() => setView(key)}>
              <Icon className="h-4 w-4 mr-1" /> {label}
            </Button>
          ))}
      </div>

      {view === 'grid' && (
        <Card>
          <CardHeader
            title={`${type} targets — ${period}`}
            subtitle={grid.data?.canSetTeam ? undefined : 'You can set your own target only.'}
          />
          <CardBody className="p-0">
            {grid.isLoading ? (
              <div className="p-6"><Skeleton className="h-48" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-body-md">
                  <thead className="bg-muted text-left text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <tr>
                      <th className="px-4 py-3">Representative</th>
                      <th className="px-4 py-3 w-40">Target count</th>
                      <th className="px-4 py-3 w-48">Target value</th>
                      <th className="px-4 py-3 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grid.data?.reps ?? []).map((rep) => {
                      const cell = cellFor(rep._id);
                      const edit = edits[rep._id] || {};
                      const count = edit.count ?? cell.monthlyTargetCount;
                      const amount = edit.amount ?? cell.monthlyTargetAmount;
                      const changed = edit.count !== undefined || edit.amount !== undefined;
                      return (
                        <tr key={rep._id} className="border-t">
                          <td className="px-4 py-2">
                            <div className="font-medium">{rep.name}</div>
                            {rep.department && <div className="text-body-sm text-muted-foreground">{rep.department}</div>}
                          </td>
                          <td className="px-4 py-2">
                            <Input type="number" min={0} value={String(count)}
                              onChange={(e) => setEdits((s) => ({ ...s, [rep._id]: { ...s[rep._id], count: Number(e.target.value) } }))} />
                          </td>
                          <td className="px-4 py-2">
                            <Input type="number" min={0} value={String(amount)}
                              onChange={(e) => setEdits((s) => ({ ...s, [rep._id]: { ...s[rep._id], amount: Number(e.target.value) } }))} />
                          </td>
                          <td className="px-4 py-2">
                            {changed && (
                              <Button size="sm" disabled={save.isPending}
                                onClick={() => save.mutate({ repId: rep._id, count, amount })}>
                                Save
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {view === 'achievement' && (
        achievement.data?.unsupported ? (
          <Card><CardBody className="flex items-start gap-2 text-body-md">
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning-text" />
            {/* Said plainly rather than shown as zeros, which would read as
                "nobody did any of this". */}
            <span>{achievement.data.reason}</span>
          </CardBody></Card>
        ) : (
          <Card>
            <CardHeader
              title={`Achievement — ${period}`}
              subtitle={achievement.data?.summary?.count
                ? `Team: ${achievement.data.summary.count.achieved} of ${achievement.data.summary.count.target} (${pct(achievement.data.summary.count.percent)})`
                : undefined}
            />
            <CardBody className="p-0">
              {achievement.isLoading ? (
                <div className="p-6"><Skeleton className="h-48" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-body-md">
                    <thead className="bg-muted text-left text-label-caps uppercase tracking-wider text-on-surface-variant">
                      <tr>
                        <th className="px-4 py-3">Representative</th>
                        <th className="px-4 py-3 text-right">Target</th>
                        <th className="px-4 py-3 text-right">Achieved</th>
                        <th className="px-4 py-3 text-right">%</th>
                        <th className="px-4 py-3 text-right">Run rate/day</th>
                        <th className="px-4 py-3 text-right">Projected</th>
                        <th className="px-4 py-3 text-right">Value achieved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Highest income first — "who brought in the most" is
                          the question this view answers, target or no target. */}
                      {[...(achievement.data?.rows ?? [])]
                        .sort((a, b) => b.amount.achieved - a.amount.achieved)
                        .map((r) => (
                        <tr key={r.representativeId} className="border-t">
                          <td className="px-4 py-2">
                            <div className="font-medium">{r.name}</div>
                            {!r.hasTarget && <div className="text-body-sm text-muted-foreground">no target set</div>}
                          </td>
                          <td className="font-mono px-4 py-2 text-right tabular-nums text-data-sm">{r.hasTarget ? r.count.target : '—'}</td>
                          <td className="font-mono px-4 py-2 text-right tabular-nums text-data-sm">{r.count.achieved}</td>
                          <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                            r.count.onTrack === true ? 'text-success-text' : r.count.onTrack === false ? 'text-danger-text' : ''
                          }`}>
                            {pct(r.count.percent)}
                          </td>
                          <td className="font-mono px-4 py-2 text-right tabular-nums text-data-sm">{r.count.runRate ?? '—'}</td>
                          <td className="font-mono px-4 py-2 text-right tabular-nums text-data-sm">{r.count.projectedClose ?? '—'}</td>
                          <td className="font-mono px-4 py-2 text-right tabular-nums text-data-sm">{formatINR(r.amount.achieved)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {achievement.data?.rows?.[0]?.progress && (
                    <p className="px-4 py-3 text-body-sm text-muted-foreground">
                      Day {achievement.data.rows[0].progress.elapsed} of {achievement.data.rows[0].progress.total}
                      {' · '}{achievement.data.rows[0].progress.remaining} remaining
                      {' · '}projections assume the current pace holds.
                    </p>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        )
      )}

      {view === 'leaderboard' && (
        <Card>
          <CardHeader title={`Leaderboard — ${period}`} />
          <CardBody className="p-0">
            {leaderboard.isLoading ? (
              <div className="p-6"><Skeleton className="h-48" /></div>
            ) : leaderboard.data?.unsupported ? (
              <p className="p-6 text-body-md text-muted-foreground">Not measurable for this target type.</p>
            ) : (
              <ul className="divide-y">
                {(leaderboard.data?.rows ?? []).map((r: any) => (
                  <li key={r.representativeId} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="w-8 text-body-md font-semibold tabular-nums text-muted-foreground">
                      {r.rank ?? '—'}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-body-md font-medium">{r.name}</span>
                    <span className="text-body-sm text-muted-foreground">
                      {r.achieved}{r.target ? ` / ${r.target}` : ''}
                    </span>
                    <span className="w-28 text-right text-body-sm text-muted-foreground tabular-nums">
                      {formatINR(r.amountAchieved ?? 0)}
                    </span>
                    <span className="w-16 text-right text-body-md font-semibold tabular-nums">
                      {r.noTarget ? '—' : pct(r.percent)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      )}

      {dirty && (
        <p className="text-body-sm text-muted-foreground">
          Unsaved changes — press Save on each row you edited.
        </p>
      )}
    </div>
  );
}
